/**
 * Customer "Request a Quote" CTA (Slice D3 — Feb 2026 July).
 *
 * Two modes:
 *   • authenticated={true}  → hits POST /api/customer/bookings/{ref}/request-quote
 *                             (JWT-required, no email prompt needed)
 *   • authenticated={false} → hits POST /api/track/{ref}/request-quote
 *                             (public; asks for the customer's email as a
 *                              soft ownership guard, matching the pattern
 *                              already used by the signed-link PDF path)
 *
 * On success the button flips to a receipt state showing "Sent to
 * {franchisee}" — customers never have to pick a franchisee themselves.
 *
 * Idempotent server-side; clicking again returns already_requested=true
 * and the same partner allocation.
 */
import { useState } from "react";
import { Send, Loader2, CheckCircle2, Sparkles, AlertCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * @param {{
 *   bookingRef: string;                     // e.g. SCWT-ABC123
 *   authenticated?: boolean;                // default true (customer dashboard)
 *   variant?: "primary" | "secondary" | "compact";
 *   className?: string;
 *   preFilledEmail?: string;                // when we already know the customer email
 *   dataTestId?: string;
 * }} props
 */
export default function RequestQuoteButton({
  bookingRef,
  authenticated = true,
  variant = "primary",
  className = "",
  preFilledEmail = "",
  dataTestId = "customer-request-quote-btn",
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [email, setEmail] = useState(preFilledEmail);
  const [preferredContact, setPreferredContact] = useState("email");
  const [comments, setComments] = useState("");
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const payload = { preferred_contact: preferredContact, comments };
      const url = authenticated
        ? `/customer/bookings/${bookingRef}/request-quote`
        : `/track/${bookingRef}/request-quote`;
      if (!authenticated) payload.customer_email = email.trim();
      const { data } = await api.post(url, payload);
      setReceipt(data);
      toast.success(
        data.already_requested
          ? "You've already requested a quote for this test."
          : "Quote request sent — your local partner will contact you.",
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Compact receipt state (never disappears once success is reached)
  if (receipt) {
    const partner = receipt.allocated_partner;
    const region = receipt.franchise_region;
    return (
      <div
        data-testid={`${dataTestId}-receipt`}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-emerald-200 bg-emerald-50 text-emerald-800 ${className}`}
      >
        <CheckCircle2 className="w-4 h-4" />
        <span>
          {partner
            ? <>Sent to <b>{partner}</b>{region && <> — {region}</>}</>
            : "Request received — an AWARE partner will reach out"}
        </span>
      </div>
    );
  }

  // ── Trigger button styles ────────────────────────────────────
  const buttonClass = {
    primary:   "inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-slate-900 hover:bg-cyan-700 text-white transition-colors",
    secondary: "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-200 text-slate-800 hover:bg-slate-50 transition-colors",
    compact:   "inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-cyan-600 hover:bg-cyan-700 text-white transition-colors",
  }[variant];

  const iconSize = variant === "compact" ? "w-3.5 h-3.5" : "w-4 h-4";

  return (
    <>
      <button
        type="button"
        data-testid={dataTestId}
        onClick={() => setOpen(true)}
        className={`${buttonClass} ${className}`}
      >
        <Sparkles className={iconSize} />
        Request a Quote
      </button>

      {open && (
        <div
          data-testid={`${dataTestId}-modal`}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => !submitting && setOpen(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
            <button
              onClick={() => !submitting && setOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-900 transition-colors"
              data-testid={`${dataTestId}-close`}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="pr-8">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Recommended action</div>
              <h3 className="text-xl font-semibold text-slate-900">Request a quote from your local Pristine Water Systems specialist</h3>
              <p className="mt-2 text-sm text-slate-600">
                We&apos;ll send your booking reference and recommended actions directly to the
                Pristine Water Systems partner allocated to your booking. They&apos;ll prepare
                and send their own quotation — AWARE doesn&apos;t calculate partner pricing.
              </p>
            </div>

            <div className="mt-5 space-y-4">
              {!authenticated && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Confirm the email on your booking</label>
                  <Input
                    data-testid={`${dataTestId}-email`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1"
                    type="email"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Used only to verify this booking belongs to you.</p>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-slate-600">Preferred contact method</label>
                <div className="mt-2 flex gap-3">
                  {[
                    { value: "email", label: "Email" },
                    { value: "phone", label: "Phone" },
                    { value: "either", label: "Either" },
                  ].map((opt) => (
                    <label key={opt.value} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="preferred-contact"
                        value={opt.value}
                        checked={preferredContact === opt.value}
                        onChange={() => setPreferredContact(opt.value)}
                        data-testid={`${dataTestId}-contact-${opt.value}`}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600">Optional comments</label>
                <textarea
                  data-testid={`${dataTestId}-comments`}
                  value={comments}
                  onChange={(e) => setComments(e.target.value.slice(0, 500))}
                  placeholder="e.g. best time to call, specific concerns…"
                  className="mt-1 w-full min-h-[70px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  maxLength={500}
                />
              </div>

              {error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 p-3 text-sm flex items-start gap-2" data-testid={`${dataTestId}-error`}>
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-none" /> {error}
                </div>
              )}

              <div className="pt-2 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  data-testid={`${dataTestId}-cancel`}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={submit}
                  disabled={submitting || (!authenticated && !email.trim())}
                  data-testid={`${dataTestId}-submit`}
                  className="bg-slate-900 hover:bg-cyan-700 text-white"
                >
                  {submitting
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    : <><Send className="w-4 h-4 mr-2" /> Send request</>}
                </Button>
              </div>

              <p className="text-[11px] text-slate-500 pt-1 leading-relaxed">
                Your local Pristine Water Systems partner controls their own retail pricing,
                installation, discounts and terms. AWARE only forwards your request and tracks
                the referral status — we don&apos;t receive or set the partner&apos;s quotation.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
